import { formatCurrency, cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, DollarSign, Sparkles, Hash } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import type { SummaryStats } from "../types";

interface FinancialIntelligenceReportProps {
    stats: SummaryStats;
    carrierPreApprovedTotal: number;
    carrierPreApprovedCount: number;
}

export function FinancialIntelligenceReport({ stats, carrierPreApprovedTotal, carrierPreApprovedCount }: FinancialIntelligenceReportProps) {
    const formatPercent = (value: number) => {
        return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
    };

    return (
        <Card className="border-2 border-gray-200 dark:border-gray-700">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg">
                            <DollarSign className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <CardTitle className="text-base font-bold">Financial Intelligence Report</CardTitle>
                            <CardDescription className="text-xs">Comprehensive financial analysis</CardDescription>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Primary Metrics - Horizontal */}
                <div>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                        <Hash className="h-3 w-3" />
                        PRIMARY METRICS
                    </h4>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center gap-1 mb-0.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                <p className="text-xs text-muted-foreground">Insurance</p>
                            </div>
                            <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{formatCurrency(stats.totalInsuranceRCV)}</p>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2 border border-gray-200 dark:border-gray-800">
                            <div className="flex items-center gap-1 mb-0.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                                <p className="text-xs text-muted-foreground">Contractor</p>
                            </div>
                            <p className="text-sm font-bold text-orange-600 dark:text-orange-400">{formatCurrency(stats.totalContractorRCV)}</p>
                        </div>

                        <div
                            className={cn(
                                "rounded-lg p-2 border",
                                stats.totalDifference > 0
                                    ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                                    : "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800"
                            )}
                        >
                            <div className="flex items-center gap-1 mb-0.5">
                                {stats.totalDifference > 0 ? (
                                    <TrendingUp className="h-3 w-3 text-red-500" />
                                ) : (
                                    <TrendingDown className="h-3 w-3 text-green-500" />
                                )}
                                <p className="text-xs text-muted-foreground">Net Difference</p>
                            </div>
                            <p
                                className={cn(
                                    "text-sm font-bold",
                                    stats.totalDifference > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"
                                )}
                            >
                                {stats.totalDifference > 0 ? "+" : "-"}
                                {formatCurrency(Math.abs(stats.totalDifference))}
                            </p>
                            <p className="text-xs text-muted-foreground">{formatPercent(stats.percentDifference)}</p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 rounded-lg p-2 border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center gap-1 mb-0.5">
                                <Sparkles className="h-3 w-3 text-purple-500" />
                                <p className="text-xs text-muted-foreground">Money Found</p>
                            </div>
                            <p className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                {formatCurrency(stats.insuranceOverscope + carrierPreApprovedTotal)}
                            </p>
                            <p className="text-xs text-purple-600 dark:text-purple-400 font-semibold">
                                {stats.overscopeItemCount + carrierPreApprovedCount} items
                            </p>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
